import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
interface StatusBadgeProps {
  isCorrect: boolean;
  motivationalText: string;
  xpGained: number;
  mode?: 'full' | 'compact';
}
/**
 * Displays the success/failure status with motivational message
 * Used in immediate feedback (full mode) and review lists (compact mode)
 */
export function StatusBadge({
  isCorrect,
  motivationalText,
  xpGained,
  mode = 'full'
}: StatusBadgeProps) {
  if (mode === 'compact') {
    // Compact mode for review lists (just an icon)
    return <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isCorrect ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'}`} data-id="element-635">
        {isCorrect ? '✓' : '✗'}
      </div>;
  }
  // Full mode for immediate feedback
  return <motion.div initial={{
    scale: 0.8,
    opacity: 0,
    y: 20
  }} animate={{
    scale: 1,
    opacity: 1,
    y: 0
  }} transition={{
    type: 'spring',
    stiffness: 200,
    damping: 15
  }} className={`
        relative overflow-hidden rounded-3xl p-6 mb-8 text-center shadow-xl border-b-4
        ${isCorrect ? 'bg-emerald-500 border-emerald-700 text-white' : 'bg-white border-slate-200 text-slate-900'}
      `} data-id="element-636">
      {isCorrect ? <>
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-20 pointer-events-none" data-id="element-637">
            <div className="absolute top-[-50%] left-[-50%] w-[200%] h-[200%] bg-[radial-gradient(circle,white,transparent)] animate-spin-slow" data-id="element-638" />
          </div>
          <div className="relative z-10 flex flex-col items-center" data-id="element-639">
            <div className="text-6xl mb-2 animate-bounce" data-id="element-640">🎉</div>
            <h2 className="text-3xl font-black mb-1 tracking-tight" data-id="element-641">
              {motivationalText}
            </h2>
            <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-1 rounded-full backdrop-blur-sm mt-2" data-id="element-642">
              <span className="font-bold text-lg" data-id="element-643">+{xpGained} XP</span>
              <Star className="w-5 h-5 fill-yellow-300 text-yellow-300" data-id="element-644" />
            </div>
          </div>
        </> : <div className="flex flex-col items-center" data-id="element-645">
          <div className="text-6xl mb-2 animate-pulse" data-id="element-646">💪</div>
          <h2 className="text-3xl font-black text-slate-800 mb-1 tracking-tight" data-id="element-647">
            {motivationalText}
          </h2>
          <p className="text-slate-500 font-medium" data-id="element-648">
            Geen zorgen, hier leer je van!
          </p>
          <div className="inline-flex items-center gap-2 bg-slate-100 px-4 py-1 rounded-full mt-3 text-slate-600" data-id="element-649">
            <span className="font-bold text-lg" data-id="element-650">+{xpGained} XP</span>
            <span className="text-xs uppercase tracking-wider" data-id="element-651">
              voor de inzet
            </span>
          </div>
        </div>}
    </motion.div>;
}